import logoCompact from '@assets/logo-compact.svg';
import logoCompactDark from '@assets/logo-compact-dark.svg';
import logoRegular from '@assets/logo-percuity.svg';
import { useBrandingStore } from '@stores/branding/stores';
import React, { useMemo } from 'react';

export enum Align {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export enum Theme {
  LIGHT = 'light',
  DARK = 'dark',
}

interface CompactProps {
  compact: true;
  size: number;
  align?: Align;
  className?: string;
  style?: React.CSSProperties;
  theme?: Theme;
  displayLeoLogo?: boolean;
  logoClassName?: string;
}

interface NonCompactProps {
  compact?: false;
  size?: never;
  align?: Align;
  className?: string;
  style?: React.CSSProperties;
  theme?: Theme;
  displayLeoLogo?: boolean;
  logoClassName?: string;
}

type Props = CompactProps | NonCompactProps;

const AppLogo: React.FC<Props> = (props) => {
  const {
    compact = false,
    size,
    align,
    className = '',
    style = {},
    theme = Theme.LIGHT,
    displayLeoLogo = false,
    logoClassName = '',
  } = props;
  const branding = useBrandingStore((state) => state.managedBranding);

  const alignStyle = useMemo(() => {
    switch (align) {
      case Align.CENTER:
        return { justifyContent: 'center' };
      case Align.RIGHT:
        return { justifyContent: 'flex-end' };
      case Align.LEFT:
        return { justifyContent: 'flex-start' };
      default:
        return { justifyContent: 'space-between' };
    }
  }, [align]);

  const logo = useMemo(() => {
    // If leoLogo is true, use default logos regardless of branding
    if (displayLeoLogo) {
      if (compact) {
        return branding?.theme_mode === Theme.DARK || theme === Theme.DARK
          ? logoCompactDark
          : logoCompact;
      }
      return logoRegular;
    }

    // Default behavior: use branding logo if available, otherwise fallback to default logos
    if (branding?.logo_url) {
      return branding.logo_url;
    }

    // Fallback to default logos when no branding logo is available
    if (compact) {
      return branding?.theme_mode === Theme.DARK || theme === Theme.DARK
        ? logoCompactDark
        : logoCompact;
    }

    return logoRegular;
  }, [branding?.logo_url, branding?.theme_mode, compact, theme, displayLeoLogo]);

  return (
    <div
      className={`flex flex-row items-center mb-0 ${className}`}
      style={{ ...alignStyle, ...style }}
    >
      <img
        data-brand-logo
        alt="Logo"
        src={logo}
        className={`w-auto h-full ${logoClassName}`}
        style={{
          ...(compact && size && { width: size, height: size }),
        }}
      />
    </div>
  );
};

export default AppLogo;
